import { useEffect, useRef } from "react";
import gsap from "gsap";
import SectionHeading from "@/components/SectionHeading";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const CARDS = [
    {
        title: "You Build",
        body: "You know Emergent well enough to teach it.",
        Icon: PetalIcon,
    },
    {
        title: "You Execute",
        body: "You've run events. On your own. No hand-holding.",
        Icon: SmileIcon,
    },
    {
        title: "You Care",
        body: "Your city's growth matters more than the title.",
        Icon: SparkleIcon,
    },
    {
        title: "You Know People",
        body: "Founders, operators, builders. You call. They show up.",
        Icon: PetalIcon,
    },
];

export default function Rooms() {
    const wrap = useRef(null);
    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.utils.toArray(".rooms-reveal").forEach((el) => {
                gsap.fromTo(
                    el,
                    { y: 40, opacity: 0 },
                    {
                        y: 0,
                        opacity: 1,
                        duration: 0.9,
                        ease: "power3.out",
                        scrollTrigger: { trigger: el, start: "top 88%" },
                    },
                );
            });
        }, wrap);
        return () => ctx.revert();
    }, []);

    return (
        <section
            id="about"
            data-testid="rooms-section"
            ref={wrap}
            className="relative py-24 md:py-32 border-t border-line bg-background overflow-hidden"
        >
            <div className="px-5 md:px-10 lg:px-14 relative max-w-[1320px] mx-auto">
                <SectionHeading
                    lineA="What Makes"
                    lineB="A VibeCity Lead"
                    accent="green"
                    testId="rooms-heading"
                    className="rooms-reveal"
                    subtitle="Not anyone. The right one."
                />

                {/* 4 cards — 2×2 grid on mobile, single row on desktop */}
                <div
                    data-testid="rooms-cards"
                    className="rooms-reveal mx-auto grid grid-cols-2 lg:grid-cols-4 max-w-[1261px] relative"
                    style={{ rowGap: 0, columnGap: 0 }}
                >
                    {/* Vertical divider down the middle on mobile */}
                    <div
                        aria-hidden
                        className="lg:hidden absolute pointer-events-none"
                        style={{
                            left: "50%",
                            top: 24,
                            bottom: 24,
                            width: 1,
                            background: "#3A3A3A",
                            transform: "translateX(-0.5px)",
                        }}
                    />
                    {/* Horizontal divider between rows on mobile */}
                    <div
                        aria-hidden
                        className="lg:hidden absolute pointer-events-none"
                        style={{
                            left: 16,
                            right: 16,
                            top: "50%",
                            height: 1,
                            background: "#3A3A3A",
                            transform: "translateY(-0.5px)",
                        }}
                    />
                    {CARDS.map((c, i) => (
                        <div
                            key={i}
                            className="flex items-stretch"
                        >
                            <Card
                                title={c.title}
                                body={c.body}
                                Icon={c.Icon}
                            />
                            {i < CARDS.length - 1 && (
                                <div
                                    aria-hidden
                                    className="hidden lg:block self-stretch shrink-0 my-auto"
                                    style={{
                                        width: "1px",
                                        height: "180px",
                                        background: "#3A3A3A",
                                    }}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Card({ title, body, Icon }) {
    return (
        <div
            data-testid={`rooms-card-${title.replace(/\s+/g, "-").replace(/\n/g, "-").toLowerCase()}`}
            className="flex flex-col items-start flex-1 min-w-0"
            style={{
                padding: "24px 20px",
                gap: "60px",
                borderRadius: 0,
                minHeight: 230,
            }}
        >
            <Icon />
            <div className="flex flex-col items-start gap-3">
                <h3
                    style={{
                        fontFamily:
                            '"Figtree", "Inter", system-ui, sans-serif',
                        fontWeight: 800,
                        fontSize: "clamp(20px, 1.55vw, 26px)",
                        lineHeight: 1.15,
                        letterSpacing: "-0.005em",
                        color: "#FFFCE1",
                        margin: 0,
                        whiteSpace: "pre-line",
                    }}
                >
                    {title}
                </h3>
                <p
                    style={{
                        fontFamily:
                            '"Figtree", "Inter", system-ui, sans-serif',
                        fontWeight: 500,
                        fontSize: "clamp(13px, 1vw, 16px)",
                        lineHeight: 1.4,
                        color: "#9A9A92",
                        margin: 0,
                        whiteSpace: "pre-line",
                    }}
                >
                    {body}
                </p>
            </div>
        </div>
    );
}

/* ---------- Icons ---------- */
function PetalIcon() {
    return (
        <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            aria-hidden
            style={{ flex: "none" }}
        >
            <g fill="#353636">
                <ellipse cx="16" cy="6.5" rx="5.6" ry="9.6" />
                <ellipse cx="16" cy="25.5" rx="5.6" ry="9.6" />
                <ellipse cx="6.5" cy="16" rx="9.6" ry="5.6" />
                <ellipse cx="25.5" cy="16" rx="9.6" ry="5.6" />
            </g>
        </svg>
    );
}

function SmileIcon() {
    return (
        <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            aria-hidden
            style={{ flex: "none" }}
        >
            <circle cx="16" cy="16" r="14" fill="#353636" />
            <circle cx="11.5" cy="13" r="1.6" fill="#0a0a0a" />
            <circle cx="20.5" cy="13" r="1.6" fill="#0a0a0a" />
            <path
                d="M10.5 19c1.6 2.2 3.5 3.2 5.5 3.2 2 0 3.9-1 5.5-3.2"
                stroke="#0a0a0a"
                strokeWidth="1.6"
                strokeLinecap="round"
                fill="none"
            />
        </svg>
    );
}

function SparkleIcon() {
    return (
        <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            aria-hidden
            style={{ flex: "none" }}
        >
            <g fill="#353636">
                <path d="M16 1 L18 13 L30 16 L18 19 L16 31 L14 19 L2 16 L14 13 Z" />
                <path
                    d="M16 4 L17.4 14.6 L28 16 L17.4 17.4 L16 28 L14.6 17.4 L4 16 L14.6 14.6 Z"
                    transform="rotate(45 16 16)"
                />
            </g>
        </svg>
    );
}
